import { IconCheck } from "@tabler/icons-react";
import { DocumentoTile } from "@/features/documentos/components/DocumentoTile";
import { Badge, Card, Divider, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import type { CFDIDeSolicitud, DocumentoVinculado } from "@/lib/types";

/** Etiqueta + color del estado fiscal general (resultado normalizado del SAT). */
const ESTADO_FISCAL: Record<string, { label: string; color: string }> = {
  Vigente: { label: "Vigente ante el SAT", color: "verde" },
  Rechazado: { label: "Rechazado por el SAT", color: "peligro" },
  Suspendido: { label: "Suspendido", color: "advertencia" },
  EnConsulta: { label: "En consulta", color: "info" },
  NoConsultado: { label: "No consultado", color: "slate" },
};

const VIGENCIA_LABEL: Record<string, string> = {
  Vigente: "Vigente",
  Cancelado: "Cancelado",
  NoEncontrado: "No encontrado",
};

/**
 * Vista de solo-lectura del CFDI ya cargado · se muestra cuando la
 * solicitud está post-envío y no se permite reemplazar. Incluye el detalle
 * y resultado de la validación contra el web service del SAT.
 */
export function CFDICargadoCard({
  cfdi,
  solicitudId,
  documentos = [],
}: {
  cfdi: CFDIDeSolicitud;
  solicitudId?: string;
  /** Documentos vinculados · se listan el XML y el PDF del CFDI para previsualizar. */
  documentos?: DocumentoVinculado[];
}) {
  // Archivos del propio CFDI (XML + PDF) · el resto del soporte documental
  // se gestiona en el paso "Documentos".
  const archivos = documentos.filter((d) => d.categoria === "FacturaXml" || d.categoria === "FacturaPdf");
  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="filled" color="multiva" size="lg">
            <IconCheck size={20} stroke={1.8} />
          </ThemeIcon>
          <div className="flex-1">
            <Text fw={600}>CFDI cargado</Text>
            <Text size="sm" c="dimmed">
              Información del CFDI vinculado a esta solicitud.
            </Text>
          </div>
        </Group>
        <Divider />
        <Stack gap={6}>
          <Group gap={6} align="baseline" wrap="wrap">
            <Text size="sm" fw={600}>
              UUID
            </Text>
            <CodigoInline>{cfdi.uuid}</CodigoInline>
          </Group>
          <Text size="sm">
            <Text span fw={600}>
              Emisor:{" "}
            </Text>
            {cfdi.nombreEmisor} · <CodigoInline>{cfdi.rfcEmisor}</CodigoInline>
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Receptor:{" "}
            </Text>
            {cfdi.nombreReceptor} · <CodigoInline>{cfdi.rfcReceptor}</CodigoInline>
          </Text>
          <Text size="sm">
            <Text span fw={600}>
              Total:{" "}
            </Text>
            ${cfdi.total.toFixed(2)} {cfdi.moneda} ·{" "}
            <Text span fw={600}>
              Subtotal:{" "}
            </Text>
            ${cfdi.subtotal.toFixed(2)}
          </Text>
          <Text size="sm" c="dimmed">
            Versión {cfdi.version} · Tipo {cfdi.tipoComprobante} · Emitido {cfdi.fechaEmision}
          </Text>
        </Stack>

        {solicitudId && archivos.length > 0 && (
          <>
            <Divider label="Archivos cargados" />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {archivos.map((d) => (
                <DocumentoTile key={d.id} solicitudId={solicitudId} doc={d} categoria={d.categoria} />
              ))}
            </div>
          </>
        )}

        <SatResultado cfdi={cfdi} />
      </Stack>
    </Card>
  );
}

function CodigoInline({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{children}</code>;
}

/** Resultado de la consulta al SAT · vigencia · cancelación · EFOS. */
function SatResultado({ cfdi }: { cfdi: CFDIDeSolicitud }) {
  const estado = ESTADO_FISCAL[cfdi.statusFiscal] ?? { label: cfdi.statusFiscal, color: "slate" };
  const vigencia = cfdi.satEstadoComprobante;
  const efos = cfdi.satValidacionEfos;
  const cancelacion = cfdi.satEstatusCancelacion;

  const tieneVigencia = !!vigencia && vigencia !== "Desconocido";
  const tieneEfos = efos === "Listado" || efos === "NoListado";
  // Evita el badge duplicado: si el estado normalizado y la vigencia del
  // comprobante dicen ambos "Vigente", solo mostramos el de vigencia.
  const estadoRedundante = cfdi.statusFiscal === "Vigente" && vigencia === "Vigente";

  return (
    <>
      <Divider label="Resultado de validación SAT" />
      <Stack gap={8}>
        <Group gap="xs">
          {!estadoRedundante && (
            <Badge variant={estado.color === "verde" ? "filled" : "light"} color={estado.color}>
              {estado.label}
            </Badge>
          )}
          {tieneVigencia && (
            <Badge variant="filled" color={vigencia === "Vigente" ? "verde" : "peligro"}>
              {VIGENCIA_LABEL[vigencia!] ?? vigencia}
            </Badge>
          )}
          {tieneEfos && (
            <Badge variant="filled" color={efos === "Listado" ? "peligro" : "verde"}>
              {efos === "Listado" ? "En lista EFOS (69-B)" : "Sin coincidencia EFOS"}
            </Badge>
          )}
          {cancelacion && (
            <Badge variant="light" color="advertencia">
              Cancelación: {cancelacion}
            </Badge>
          )}
        </Group>

        {cfdi.statusFiscalCodigo && (
          <Text size="sm">
            <Text span fw={600}>
              Código SAT:{" "}
            </Text>
            <CodigoInline>{cfdi.statusFiscalCodigo}</CodigoInline>
          </Text>
        )}
        {cfdi.statusFiscalMensajeAmigable && <Text size="sm">{cfdi.statusFiscalMensajeAmigable}</Text>}
        {cfdi.statusFiscalMensajeOriginal && (
          <Text size="xs" c="dimmed">
            Respuesta del SAT: {cfdi.statusFiscalMensajeOriginal}
          </Text>
        )}
        {cfdi.satConsultadoAt ? (
          <Text size="xs" c="dimmed">
            Consultado el {new Date(cfdi.satConsultadoAt).toLocaleString("es-MX")}
          </Text>
        ) : cfdi.statusFiscalConsultadoAt ? (
          <Text size="xs" c="dimmed">
            Consultado el {new Date(cfdi.statusFiscalConsultadoAt).toLocaleString("es-MX")}
          </Text>
        ) : null}
      </Stack>
    </>
  );
}
